import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Card, Col, ProgressBar, Row, Spinner } from 'react-bootstrap';

import {
  CancelPushBroadcastMutation,
  CancelPushBroadcastMutationVariables,
  PushBroadcastStatus,
  RetryFailedPushBroadcastBatchesMutation,
  RetryFailedPushBroadcastBatchesMutationVariables,
} from '../../../../../../__gqltypes__';
import { CANCEL_PUSH_BROADCAST, RETRY_FAILED_PUSH_BROADCAST_BATCHES } from '../graphql';
import { PUSH_BROADCAST_ACTIVE_STATUSES, pushBroadcastErrorIssues, PushBroadcastData, PushIssue } from '../utils';
import { PushIssueList } from './PushIssueList';

type Props = {
  broadcast: PushBroadcastData;
  onChanged: (broadcast: PushBroadcastData) => void;
};

// Statuses a cancel still stops.
const CANCELLABLE: ReadonlyArray<PushBroadcastStatus> = [
  PushBroadcastStatus.SCHEDULED,
  ...PUSH_BROADCAST_ACTIVE_STATUSES,
];

export const PUSH_CANCEL_CONFIRM =
  'Stop this broadcast? Batches not sent yet will not go out.\n\nPlayers already sent cannot be recalled.';

function Figure({ label, value, className = '' }: { label: string; value: number; className?: string }) {
  return (
    <Col xs={6} md={4} className="mb-2">
      <div className={`h5 mb-0 ${className}`}>{value.toLocaleString()}</div>
      <small className="text-muted">{label}</small>
    </Col>
  );
}

/** A started broadcast: its batches by status (polled by the page), Cancel throughout the send, Retry when failed. */
export function PushProgress({ broadcast, onChanged }: Props): JSX.Element {
  const [issues, setIssues] = useState<PushIssue[]>([]);
  const [cancelBroadcast, { loading: cancelling }] = useMutation<
    CancelPushBroadcastMutation,
    CancelPushBroadcastMutationVariables
  >(CANCEL_PUSH_BROADCAST);
  const [retryBatches, { loading: retrying }] = useMutation<
    RetryFailedPushBroadcastBatchesMutation,
    RetryFailedPushBroadcastBatchesMutationVariables
  >(RETRY_FAILED_PUSH_BROADCAST_BATCHES);

  const { progress, status } = broadcast;
  const isActive = PUSH_BROADCAST_ACTIVE_STATUSES.includes(status);
  const canCancel = CANCELLABLE.includes(status);
  const canRetry =
    !!progress &&
    progress.batchesFailed > 0 &&
    [PushBroadcastStatus.SENDING, PushBroadcastStatus.FAILED].includes(status);

  const cancel = async () => {
    if (!window.confirm(PUSH_CANCEL_CONFIRM)) {
      return;
    }
    setIssues([]);
    try {
      const result = await cancelBroadcast({ variables: { id: broadcast.id } });
      if (result.data) {
        onChanged(result.data.cancelPushBroadcast);
      }
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    }
  };

  const retry = async () => {
    setIssues([]);
    try {
      const result = await retryBatches({ variables: { id: broadcast.id } });
      if (result.data) {
        onChanged(result.data.retryFailedPushBroadcastBatches);
      }
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    }
  };

  const total = progress?.batchesTotal ?? 0;
  const percent = (value: number) => (total > 0 ? (value * 100) / total : 0);

  return (
    <Card className={`mb-3 ${isActive ? 'border-primary' : ''}`}>
      <Card.Header className="d-flex align-items-center">
        <span className="font-weight-bold mr-2">Delivery</span>
        {isActive ? <Spinner animation="border" size="sm" className="mr-2" /> : null}
        <span className="text-muted small">
          {status === PushBroadcastStatus.PREPARING ? 'Preparing the recipient list…' : null}
          {status === PushBroadcastStatus.SENDING ? 'In progress, refreshed every few seconds' : null}
        </span>
        <span className="ml-auto">
          {canRetry ? (
            <Button size="sm" variant="warning" className="mr-2" disabled={retrying} onClick={retry}>
              Retry failed batches ({progress?.batchesFailed})
            </Button>
          ) : null}
          {canCancel ? (
            <Button size="sm" variant="danger" disabled={cancelling} onClick={cancel}>
              {cancelling ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
              Cancel the send
            </Button>
          ) : null}
        </span>
      </Card.Header>
      <Card.Body>
        {canCancel ? (
          <div className="small text-muted mb-2">
            Cancel stops the batches not sent yet (within one chunk of 100 players). Players already sent cannot be
            recalled.
          </div>
        ) : null}
        <PushIssueList issues={issues} title="The server refused" />
        {broadcast.lastError ? <Alert variant="danger">{broadcast.lastError}</Alert> : null}
        {progress && total > 0 ? (
          <>
            <ProgressBar className="mb-1" style={{ height: '1.25rem' }}>
              <ProgressBar variant="success" now={percent(progress.batchesCompleted)} key="completed" />
              <ProgressBar variant="primary" animated now={percent(progress.batchesRunning)} key="running" />
              <ProgressBar variant="danger" now={percent(progress.batchesFailed)} key="failed" />
              <ProgressBar variant="dark" now={percent(progress.batchesCancelled)} key="cancelled" />
            </ProgressBar>
            <div className="small mb-3">
              {total.toLocaleString()} batches: {progress.batchesCompleted.toLocaleString()} completed ·{' '}
              {progress.batchesRunning.toLocaleString()} running · {progress.batchesPending.toLocaleString()} waiting ·{' '}
              <span className={progress.batchesFailed > 0 ? 'text-danger font-weight-bold' : ''}>
                {progress.batchesFailed.toLocaleString()} failed
              </span>{' '}
              · {progress.batchesCancelled.toLocaleString()} cancelled
            </div>
            <Row>
              <Figure
                label={`players sent of ${(broadcast.recipientsCount ?? 0).toLocaleString()}`}
                value={progress.recipientsSent}
              />
              <Figure label="devices targeted" value={progress.devicesTargeted} />
              <Figure label="devices accepted by FCM" value={progress.devicesAccepted} className="text-success" />
              <Figure label="devices rejected" value={progress.devicesRejected} />
              <Figure label="held back by FCM quota, resent after a pause" value={progress.devicesPending} />
              <Figure label="dead tokens pruned" value={progress.tokensPruned} />
            </Row>
          </>
        ) : (
          <div className="text-muted">
            {status === PushBroadcastStatus.PREPARING ? 'No batch yet.' : 'Nothing was sent.'}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
